import { Link } from "react-router-dom";
import { Linkedin, ArrowRight, ExternalLink } from "lucide-react";

const team = [
  {
    name: "Kalen J Johnson",
    title: "Co-Founder & Board Chair",
    photo: "https://customer-assets.emergentagent.com/job_helocore-core/artifacts/imt4vri6_Screenshot%202026-04-05%20at%201.46.19%E2%80%AFAM.png",
    linkedin: "https://www.linkedin.com/in/kalenjohnson/",
    bio: "Kalen is a seasoned executive and strategic advisor who has served as Partner, Chair, and Board Member across organizations in both the private and public sectors. She holds a Master of Finance from Queen's University's Smith School of Business and a BSc (Honours) from the University of Guelph. She is also the founder of the Johnson Foundation.",
  },
  {
    name: "Cameron Bergsma",
    title: "Founder & CEO",
    photo: "https://customer-assets.emergentagent.com/job_helocore-core/artifacts/onih8noq_Screenshot%202026-04-05%20at%201.46.43%E2%80%AFAM.png",
    linkedin: "https://www.linkedin.com/in/cameronbergsma/",
    bio: "Cameron leads Helocore's vision to reshape how developers write, ship, and scale software. With a background in cloud infrastructure and developer tooling, he founded Helocore to solve the fragmentation he experienced firsthand — building a single platform that unifies the development lifecycle from IDE to global deployment.",
  },
];

const companyFacts = [
  ["Legal entity", "Helocore Inc."],
  ["Jurisdiction", "Federal Canadian"],
  ["Corporation No.", "1781131-4"],
  ["Business No.", "792360372"],
  ["Incorporated", "March 29, 2026"],
  ["Parent company", "Aikaro Technologies Corp."],
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">

      {/* Hero — Google style: big text, lots of whitespace, subtle accent shape */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#fff8f3] rounded-full -translate-y-1/3 translate-x-1/4 pointer-events-none" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 sm:pt-28 pb-20 sm:pb-24">
          <div className="max-w-[640px]">
            <h1 className="text-[36px] sm:text-[46px] lg:text-[56px] font-normal text-[#202124] leading-[1.12] tracking-[-0.5px]">
              About Helocore
            </h1>
            <p className="mt-6 text-[17px] sm:text-[19px] text-[#5f6368] leading-[1.65] font-light">
              We're building a unified cloud platform for developers — from writing your first line of code to running it in 300+ cities around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Stats strip — Google style thin divider with numbers */}
      <section className="border-y border-[#e8eaed]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-3 divide-x divide-[#e8eaed]">
            {[
              ["2", "Core products"],
              ["300+", "Cities at launch"],
              ["2026", "Year founded"],
            ].map(([num, label]) => (
              <div key={label} className="py-8 sm:py-10 text-center">
                <p className="text-[28px] sm:text-[36px] font-normal text-[#202124]">{num}</p>
                <p className="text-[13px] sm:text-[14px] text-[#5f6368] mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission — clean two-column */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-2">
              <p className="text-[13px] font-medium text-[#6C5CE7] tracking-wide uppercase">Our mission</p>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] leading-[1.2] mb-6">
                Give every developer a platform that just works.
              </h2>
              <div className="space-y-5 text-[16px] text-[#5f6368] leading-[1.7]">
                <p>
                  The tools developers rely on every day — editors, CI/CD pipelines, hosting providers, monitoring dashboards — were never designed to work together. We built Helocore to change that.
                </p>
                <p>
                  And with the Aethernet, we're introducing infrastructure that anyone can be part of and earn from. Not just another cloud provider — a new model for how the internet works.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products — card style similar to Google Cloud product cards */}
      <section className="bg-[#f8f9fa] py-20 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20 mb-14">
            <div className="lg:col-span-2">
              <p className="text-[13px] font-medium text-[#6C5CE7] tracking-wide uppercase">Products</p>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] leading-[1.2]">
                Two products. One platform.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Studio Card */}
            <Link to="/studio" className="group bg-white rounded-xl border border-[#e8eaed] p-8 sm:p-10 hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#fff4eb] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                </div>
                <span className="text-[11px] font-semibold text-white bg-[#34a853] px-2.5 py-0.5 rounded-full uppercase tracking-wide">In Alpha</span>
              </div>
              <h3 className="text-[22px] font-normal text-[#202124] mb-3 group-hover:text-[#6C5CE7] transition-colors">Helocore Studio</h3>
              <p className="text-[15px] text-[#5f6368] leading-[1.6] mb-6">
                Cloud-native IDE with full-context AI, real-time collaboration, and one-click deploys. Everything a developer needs — in one tab.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#34a853] font-medium">Closed Beta — June 1, 2026</span>
                <ArrowRight className="w-4 h-4 text-[#dadce0] group-hover:text-[#6C5CE7] group-hover:translate-x-1 transition-all" />
              </div>
            </Link>

            {/* Cloud Card */}
            <Link to="/cloud" className="group bg-white rounded-xl border border-[#e8eaed] p-8 sm:p-10 hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)] transition-shadow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#f0f9f1] flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#34a853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>
                </div>
                <span className="text-[11px] font-semibold text-white bg-[#80868b] px-2.5 py-0.5 rounded-full uppercase tracking-wide">Coming Soon</span>
              </div>
              <h3 className="text-[22px] font-normal text-[#202124] mb-3 group-hover:text-[#6C5CE7] transition-colors">Helocore Cloud</h3>
              <p className="text-[15px] text-[#5f6368] leading-[1.6] mb-6">
                Edge hosting on the Aethernet — a patent-pending network where anyone can own the infrastructure and earn from it. 300+ cities, sub-50ms latency.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#80868b] font-medium">Closed Alpha — November 2026</span>
                <ArrowRight className="w-4 h-4 text-[#dadce0] group-hover:text-[#6C5CE7] group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Leadership — Google style: restrained, structured, left-aligned label */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20 mb-16">
            <div className="lg:col-span-2">
              <p className="text-[13px] font-medium text-[#6C5CE7] tracking-wide uppercase">Leadership</p>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] leading-[1.2]">
                The people behind Helocore
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-16">
            {team.map((person) => (
              <div key={person.name} className="flex gap-6" data-testid={`team-member-${person.name.split(" ")[0].toLowerCase()}`}>
                <div className="w-[140px] sm:w-[160px] flex-shrink-0 self-stretch">
                  <div className="w-full h-full rounded-xl overflow-hidden bg-[#f1f3f4]">
                    <img src={person.photo} alt={person.name} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[18px] font-normal text-[#202124]">{person.name}</h3>
                  <p className="text-[14px] text-[#5f6368] mt-0.5">{person.title}</p>
                  <p className="text-[14px] text-[#5f6368] leading-[1.7] mt-4">
                    {person.bio}
                  </p>
                  <a
                    href={person.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[14px] text-[#6C5CE7] hover:underline mt-4"
                    data-testid={`linkedin-${person.name.split(" ")[0].toLowerCase()}`}
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                    LinkedIn
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="bg-[#f8f9fa] py-20 sm:py-28 border-t border-[#e8eaed]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-2">
              <p className="text-[13px] font-medium text-[#6C5CE7] tracking-wide uppercase">Corporate</p>
              <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] leading-[1.2] mt-3">
                Company information
              </h2>
            </div>
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl border border-[#e8eaed]">
                {companyFacts.map(([label, value], i) => (
                  <div
                    key={label}
                    className={`flex items-center justify-between px-6 sm:px-8 py-5 ${i < companyFacts.length - 1 ? "border-b border-[#f1f3f4]" : ""}`}
                  >
                    <span className="text-[14px] text-[#5f6368]">{label}</span>
                    <span className="text-[14px] font-medium text-[#202124]">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact — minimal, Google-style */}
      <section className="py-20 sm:py-24 border-t border-[#e8eaed]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
            <div className="lg:col-span-2">
              <p className="text-[13px] font-medium text-[#6C5CE7] tracking-wide uppercase">Contact</p>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] leading-[1.2] mb-6">
                Get in touch
              </h2>
              <p className="text-[16px] text-[#5f6368] leading-[1.7] mb-8">
                For privacy, partnership, or legal inquiries, reach us at the emails below.
              </p>
              <div className="space-y-3">
                <a href="mailto:privacy@aikarotech.com" className="flex items-center gap-4 group">
                  <span className="text-[14px] text-[#5f6368] w-16">Privacy</span>
                  <span className="text-[14px] text-[#6C5CE7] group-hover:underline">privacy@aikarotech.com</span>
                </a>
                <a href="mailto:legal@aikarotech.com" className="flex items-center gap-4 group">
                  <span className="text-[14px] text-[#5f6368] w-16">Legal</span>
                  <span className="text-[14px] text-[#6C5CE7] group-hover:underline">legal@aikarotech.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
